import { useEffect, useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { Modal } from "@impero-com/ui";
import { Switch } from "antd";

import { CommentField } from "components/modals/CommentField";
import { Content } from "components/modals/Content";
import { Footer } from "components/modals/Footer";
import { useModalComment } from "components/modals/useModalComment";
import { CONSTANTS } from "utils/constants";

import * as theme from "./ControlActivityReminderModal.css";

const MAX_MESSAGE_LENGTH =
    CONSTANTS.CONTROL_CONSTANTS.MAX_REMINDER_CUSTOM_MESSAGE_CHARS;

interface ControlActivityReminderModalProps {
    open: boolean;
    loading: boolean;
    onConfirm: (customMessage?: string) => void;
    onClose: () => void;
}

export function ControlActivityReminderModal({
    open,
    loading,
    onConfirm,
    onClose,
}: ControlActivityReminderModalProps) {
    const { t } = useTranslation();
    const [personalized, setPersonalized] = useState(false);
    const { comment, setComment, trimmed, isValid, isTooLong } =
        useModalComment({
            open,
            required: personalized,
            maxLength: MAX_MESSAGE_LENGTH,
        });

    // Reset the choice each time the modal opens.
    useEffect(() => {
        if (open) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setPersonalized(false);
        }
    }, [open]);

    return (
        <Modal
            variant="content"
            open={open}
            footer={
                <Footer
                    onClose={onClose}
                    onConfirm={() =>
                        onConfirm(personalized ? trimmed : undefined)
                    }
                    confirmLabel={t("ControlResult_Remind")}
                    loading={loading}
                    okDisabled={personalized && !isValid}
                />
            }
            onClose={onClose}
            title={t("ControlResult_Remind_ConfirmTitle")}
        >
            <Content body={t("ControlResult_Remind_ConfirmBody")}>
                <div className={theme.personalizedToggle}>
                    <Switch checked={personalized} onChange={setPersonalized} />
                    <span>{t("ControlResult_Remind_Personalized")}</span>
                </div>
                {personalized && (
                    <CommentField
                        value={comment}
                        onChange={setComment}
                        label={t("ControlResult_Remind_PersonalizedMessage")}
                        placeholder={t(
                            "ControlResult_Remind_PersonalizedPlaceholder"
                        )}
                        error={
                            isTooLong
                                ? t("ControlResult_Comment_Error")
                                : undefined
                        }
                    />
                )}
            </Content>
        </Modal>
    );
}
